/**
 * Talk to your log: connecting the parent's own AI assistant. Pick the one you
 * use, copy one message into it, and it walks you through adding Babia (the
 * message names the connector URL and the guide at /connect.md); or follow the
 * steps here. Then a few things to try saying, in this household's own names.
 * Nothing here is a 3am flow, and nothing is sent to an assistant from here.
 */
import { Link } from "@tanstack/react-router";
import { Check, Copy, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { markAssistantTipSeen } from "../components/log/assistant-tip.tsx";
import { CopyUrl } from "../components/landing/connect.tsx";
import { Button } from "../components/ui/button.tsx";
import { useToast } from "../components/ui/toast.tsx";
import { track } from "../lib/analytics.ts";
import {
  ASSISTANTS,
  type AssistantId,
  CONNECT_PROMPT,
  MCP_URL,
  tryPhrases,
} from "../lib/assistants.ts";
import { useBabies, useBottleUnit } from "../lib/queries.ts";
import { cn } from "../lib/utils.ts";

const STORAGE_KEY = "babia.assistant";

function readPick(): AssistantId | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return ASSISTANTS.some((a) => a.id === stored) ? (stored as AssistantId) : null;
  } catch {
    return null;
  }
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="mt-7 border-t border-ink pt-3">
      <div className="kicker">Step {n}</div>
      <h3 className="heading mt-1 text-[20px]">{title}</h3>
      <div className="mt-1.5 text-[16px]">{children}</div>
    </li>
  );
}

/** The message to paste, large enough to read, with one big Copy button. */
function CopyPrompt({ assistant }: { assistant: AssistantId }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <>
      <p className="mt-2 select-all border border-divider bg-surface px-4 py-3 text-[16px] leading-snug">
        {CONNECT_PROMPT}
      </p>
      <Button
        variant="primary"
        className="mt-3 w-full"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(CONNECT_PROMPT);
            setCopied(true);
            track("assistant prompt copied", { assistant });
          } catch {
            toast.show("Couldn't copy — press and hold the message to select it");
          }
        }}
      >
        {copied ? (
          <Check className="size-5" aria-hidden="true" />
        ) : (
          <Copy className="size-5" aria-hidden="true" />
        )}
        <span aria-live="polite">{copied ? "Copied" : "Copy message"}</span>
      </Button>
    </>
  );
}

export function Assistant() {
  const babies = useBabies();
  const unit = useBottleUnit();
  const [pick, setPick] = useState<AssistantId | null>(readPick);
  useEffect(markAssistantTipSeen, []);

  function choose(id: AssistantId) {
    setPick(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // the choice still holds for this visit
    }
  }

  const assistant = ASSISTANTS.find((a) => a.id === pick);
  const names = (babies.data ?? []).map((b) => b.name);

  return (
    <div className="mx-auto max-w-[720px] pt-2">
      <div className="flex items-baseline">
        <h1 className="heading flex items-center gap-2 text-[26px] tracking-[-0.02em]">
          <Sparkles className="size-6 shrink-0 text-accent-2" aria-hidden="true" />
          Talk to your log
        </h1>
        <Link to="/" className="ghost-btn ml-auto px-2 py-2 text-[14px]">
          ← Log
        </Link>
      </div>
      <div className="head-rule" />

      <p className="mt-5 text-[17px] leading-snug">
        Connect the AI assistant you already use, then just say what happened. It goes on this log,
        on every phone in your household, the same as a tap. You can ask it about the log, too.
      </p>

      <section className="mt-8" aria-labelledby="pick">
        <h2 id="pick" className="kicker">
          Which assistant do you use?
        </h2>
        <div className="mt-2.5 grid grid-cols-2 gap-2 min-[480px]:grid-cols-3">
          {ASSISTANTS.map((a) => (
            <Button
              key={a.id}
              variant="choice"
              aria-pressed={pick === a.id}
              className={cn("!min-h-[52px] text-[17px]", pick === a.id && "is-selected")}
              data-ph-capture-attribute-assistant={a.id}
              onClick={() => choose(a.id)}
            >
              {a.name}
            </Button>
          ))}
        </div>
      </section>

      {assistant?.connects ? (
        <ol aria-label={`Connecting ${assistant.name}`}>
          {assistant.needs ? (
            <li className="mt-7 border-l-[3px] border-accent-2 pl-3 text-[15px]">
              <span className="kicker block">Before you start</span>
              {assistant.needs}
            </li>
          ) : null}
          {assistant.addUrl ? (
            <>
              <Step n={1} title={`Open ${assistant.name}’s connector page`}>
                <a
                  href={assistant.addUrl}
                  target="_blank"
                  rel="noopener"
                  className="primary-btn heading mt-1 flex min-h-[52px] w-full items-center justify-center px-4 text-[17px]"
                  onClick={() => track("assistant add link opened", { assistant: assistant.id })}
                >
                  Add Babia to {assistant.name}
                </a>
                <p className="mt-2">
                  {assistant.name} opens with Babia’s name and address filled in. Check them, then
                  click Add.
                </p>
              </Step>
              <Step n={2} title="Connect, sign in, and allow">
                <p>
                  Click Connect next to Babia. Sign in with the same Google or Facebook account you
                  use here, then tap Allow.
                </p>
              </Step>
            </>
          ) : (
            <>
              <Step n={1} title="Copy this message">
                <CopyPrompt assistant={assistant.id} />
              </Step>
              <Step
                n={2}
                title={
                  assistant.url ? `Paste it into ${assistant.name}` : "Paste it into your assistant"
                }
              >
                <p>
                  Paste it into a new chat
                  {assistant.url ? (
                    <>
                      {" "}
                      (
                      <a
                        href={assistant.url}
                        target="_blank"
                        rel="noopener"
                        className="text-accent underline underline-offset-4"
                      >
                        open {assistant.name}
                      </a>
                      )
                    </>
                  ) : null}
                  . It walks you through adding Babia, one step at a time.
                </p>
              </Step>
              <Step n={3} title="Sign in and tap Allow">
                <p>
                  When Babia’s page opens, sign in with the same Google or Facebook account you use
                  here, then tap Allow. That’s it.
                </p>
              </Step>
            </>
          )}
          <li className="mt-7">
            <details className="group border-t border-rule">
              <summary className="heading flex min-h-[52px] cursor-pointer list-none items-center gap-3 py-2 text-[17px] [&::-webkit-details-marker]:hidden">
                <span className="min-w-0 flex-1">
                  {assistant.addUrl
                    ? `Stuck? Let ${assistant.name} walk you through it`
                    : "Rather do it yourself?"}
                </span>
                <span
                  aria-hidden="true"
                  className="text-[22px] font-normal leading-none text-accent group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              {assistant.addUrl ? (
                <div className="pb-2 text-[15px]">
                  <p>Copy this message into a new chat, and it explains each step.</p>
                  <CopyPrompt assistant={assistant.id} />
                </div>
              ) : null}
              <p className={cn("text-[15px]", assistant.addUrl && "mt-5")}>The steps by hand:</p>
              <ol className="list-decimal pb-2 pl-6 text-[15px]">
                {assistant.steps.map((step) => (
                  <li key={step} className="mt-1.5 pl-1">
                    {step}
                  </li>
                ))}
              </ol>
              <p className="mt-2 text-[14px] opacity-75">Babia’s address:</p>
              <CopyUrl url={MCP_URL} />
            </details>
          </li>
        </ol>
      ) : assistant ? (
        <section className="mt-7 border-t border-ink pt-3">
          <h3 className="heading text-[20px]">{assistant.name} can’t connect to Babia yet</h3>
          <p className="mt-1.5 text-[16px]">{assistant.why}</p>
          <p className="mt-2 text-[16px]">
            Claude, ChatGPT, and Gemini can: pick one above. Claude is the quickest, and works on
            its free plan. The app does everything on its own, too.
          </p>
        </section>
      ) : (
        <p className="mt-4 text-[15px] opacity-75">
          Don’t use one yet? Claude is the quickest to set up, and works on its free plan.
        </p>
      )}

      <section className="mt-12" aria-labelledby="try">
        <h2 id="try" className="kicker">
          Once it’s connected, try saying
        </h2>
        <ul className="mt-2.5">
          {tryPhrases(names, unit).map((phrase) => (
            <li key={phrase} className="border-t border-rule py-2.5 text-[17px] italic">
              “{phrase}”
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-8 border-t border-divider pt-4 text-[14px] opacity-70">
        Your assistant can log, read, fix, and delete entries, and pass your feedback on to us, once
        you allow it; it can’t change your babies, settings, or partner. Each partner connects their
        own. Babia never calls an AI model itself: your assistant does the understanding, under its
        provider’s privacy policy. See the{" "}
        <Link to="/privacy" className="underline underline-offset-4">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
